import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Alert,
  TextField,
  Grid,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Security,
  Computer,
} from '@mui/icons-material';
import { useQuery, useMutation } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const IpApprovalPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { user, isLoading: authLoading, isAuthenticated } = useAuth();
  const [finalCidr, setFinalCidr] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  
  // Check if token exists in localStorage
  const hasAuthToken = !!localStorage.getItem('token');

  // Check if user is admin
  const isAdministrator = user?.role && ['administrator', 'super_user'].includes(user.role);

  // Fetch approval request details
  const { data: requestData, isLoading, error } = useQuery({
    queryKey: ['ipApprovalRequest', token],
    queryFn: () => apiClient.ipApproval.getRequest(token!),
    enabled: !!token,
  });

  // Approve mutation
  const approveMutation = useMutation({
    mutationFn: (cidr: string) => apiClient.ipApproval.approve(token!, cidr),
    onSuccess: () => {
      // Redirect to network config page or show success
      setTimeout(() => {
        navigate('/system/network-config');
      }, 2000);
    },
    onError: (error: any) => {
      console.error('Error approving IP:', error);
      // Error will be displayed in the UI below
    },
  });

  // Reject mutation
  const rejectMutation = useMutation({
    mutationFn: (reason: string) => apiClient.ipApproval.reject(token!, reason),
    onSuccess: () => {
      // Show success message
      setTimeout(() => {
        navigate('/system/network-config');
      }, 2000);
    },
    onError: (error: any) => {
      console.error('Error rejecting IP:', error);
      // Error will be displayed in the UI below
    },
  });

  // Set default CIDR when request data loads
  useEffect(() => {
    if (requestData?.data?.data) {
      const request = requestData.data.data;
      // If no requestedCidr or it's /32 format, convert to /24 subnet format
      if (!request.requestedCidr || request.requestedCidr.endsWith('/32')) {
        const ipParts = request.ipAddress.split('.');
        const subnetBase = `${ipParts[0]}.${ipParts[1]}.${ipParts[2]}.0`;
        setFinalCidr(`${subnetBase}/24`);
      } else {
        setFinalCidr(request.requestedCidr);
      }
    }
  }, [requestData]);

  // Verify authentication token exists when component mounts or user changes
  useEffect(() => {
    if (!authLoading && user) {
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('⚠️ User is logged in but token is missing from localStorage');
        // Force re-authentication
        navigate(`/login?returnUrl=${encodeURIComponent(window.location.pathname + window.location.search)}`);
      }
    }
  }, [authLoading, user, navigate]);

  // Wait for auth to finish loading
  if (authLoading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!user || !hasAuthToken || !isAuthenticated) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          You must be logged in to approve IP requests.
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => navigate(`/login?returnUrl=${encodeURIComponent(window.location.pathname + window.location.search)}`)}
        >
          Go to Login
        </Button>
      </Container>
    );
  }

  if (!isAdministrator) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          Access denied. Only administrators can approve IP requests.
        </Alert>
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Check if request was already processed (approved, rejected, or expired)
  // Backend now returns 200 OK with status flags instead of 400 errors
  const requestDataObj = requestData?.data?.data;
  const requestStatus = requestDataObj?.status;
  const isAlreadyApproved = requestDataObj?.isAlreadyApproved || false;
  const isAlreadyRejected = requestDataObj?.isAlreadyRejected || false;
  const isExpired = requestDataObj?.isExpired || false;
  const statusMessage = (requestData?.data as any)?.message;

  // Handle already processed requests (approved, rejected, expired)
  if (isAlreadyApproved || isAlreadyRejected || isExpired || (requestStatus && ['approved', 'rejected', 'expired'].includes(requestStatus))) {
    const statusMessages: Record<string, string> = {
      approved: statusMessage || 'This IP approval request has already been approved and is active in the network configuration.',
      rejected: statusMessage || 'This IP approval request has already been rejected.',
      expired: statusMessage || 'This IP approval request has expired.'
    };
    
    const severityMap: Record<string, 'success' | 'warning' | 'info'> = {
      approved: 'success',
      rejected: 'warning',
      expired: 'info'
    };
    
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Alert 
          severity={severityMap[requestStatus || 'approved'] || 'info'}
          sx={{ mb: 2 }}
        >
          {statusMessages[requestStatus || 'approved'] || 'This request has already been processed.'}
        </Alert>
        <Button variant="contained" onClick={() => navigate('/system/network-config')}>
          Go to Network Configuration
        </Button>
      </Container>
    );
  }

  // Handle actual errors
  if (error || !requestDataObj) {
    const errorMessage = error && (error as any)?.response?.data?.message;
    
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          {errorMessage || (error ? 'Failed to load approval request' : 'Approval request not found or invalid')}
        </Alert>
      </Container>
    );
  }

  const request = requestDataObj;

  const handleApprove = async () => {
    // Double-check authentication and token before proceeding
    const currentToken = localStorage.getItem('token');
    if (!currentToken || !hasAuthToken || !isAuthenticated || !user) {
      console.error('Authentication check failed:', { 
        hasToken: !!currentToken, 
        hasAuthToken, 
        isAuthenticated, 
        hasUser: !!user 
      });
      alert('You must be logged in to approve IP requests. Redirecting to login...');
      navigate(`/login?returnUrl=${encodeURIComponent(window.location.pathname + window.location.search)}`);
      return;
    }

    if (!finalCidr.trim()) {
      alert('Please enter a valid CIDR format (e.g., 192.168.1.0/24)');
      return;
    }

    // Validate CIDR format
    const cidrRegex = /^(?:25[0-5]|2[0-4]\d|1?\d?\d)(?:\.(?:25[0-5]|2[0-4]\d|1?\d?\d)){3}\/(?:3[0-2]|[12]?\d)$/;
    if (!cidrRegex.test(finalCidr.trim())) {
      alert('Invalid CIDR format. Must be in format: x.x.x.x/xx (e.g., 192.168.1.0/24 or 192.168.1.100/32)');
      return;
    }

    if (window.confirm(`Are you sure you want to approve adding ${finalCidr.trim()} to the network configuration?`)) {
      // Verify token is still valid before making request
      const tokenBeforeRequest = localStorage.getItem('token');
      if (!tokenBeforeRequest) {
        alert('Your session has expired. Please log in again.');
        navigate(`/login?returnUrl=${encodeURIComponent(window.location.pathname + window.location.search)}`);
        return;
      }
      approveMutation.mutate(finalCidr.trim());
    }
  };

  const handleReject = () => {
    // Check authentication before proceeding
    if (!hasAuthToken || !isAuthenticated) {
      alert('You must be logged in to reject IP requests. Redirecting to login...');
      navigate(`/login?returnUrl=${encodeURIComponent(window.location.pathname + window.location.search)}`);
      return;
    }

    if (window.confirm('Are you sure you want to reject this IP approval request?')) {
      rejectMutation.mutate(rejectionReason || 'Rejected by administrator');
    }
  };

  const handleCancel = () => {
    navigate('/system/network-config');
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Security color="primary" sx={{ fontSize: 40 }} />
        <Typography variant="h4">IP Approval Request</Typography>
      </Box>

      {approveMutation.isSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="body1" fontWeight="bold">
            ✓ IP Address Approved
          </Typography>
          <Typography variant="body2">
            {finalCidr} has been added to the network configuration. Redirecting...
          </Typography>
        </Alert>
      )}

      {rejectMutation.isSuccess && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body1" fontWeight="bold">
            Request Rejected
          </Typography>
          <Typography variant="body2">
            The IP approval request has been rejected. Redirecting...
          </Typography>
        </Alert>
      )}

      {(approveMutation.isError || rejectMutation.isError) && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="body1" fontWeight="bold">
            Action Failed
          </Typography>
          <Typography variant="body2">
            {approveMutation.error?.response?.data?.message || 
             rejectMutation.error?.response?.data?.message || 
             'An error occurred. Please try again or ensure you are logged in.'}
          </Typography>
          {approveMutation.error?.response?.status === 401 || rejectMutation.error?.response?.status === 401 ? (
            <Button 
              variant="contained" 
              sx={{ mt: 2 }} 
              onClick={() => navigate(`/login?returnUrl=${encodeURIComponent(window.location.pathname + window.location.search)}`)}
            >
              Go to Login
            </Button>
          ) : null}
        </Alert>
      )}

      {requestData?.data?.data?.needsReapproval && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body1" fontWeight="bold">
            ⚠️ Re-approval Required
          </Typography>
          <Typography variant="body2">
            This IP address was previously approved but has been removed from the network configuration. 
            Please approve it again to add it back to the network configuration.
          </Typography>
        </Alert>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Computer color="primary" />
            Request Details
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Employee Name
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {request.userName}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Email
              </Typography>
              <Typography variant="body1">
                {request.userEmail}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                IP Address
              </Typography>
              <Typography variant="body1" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
                {request.ipAddress}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Work Location
              </Typography>
              <Typography variant="body1">
                {request.workLocation === 'on_site' ? 'On-Site' : request.workLocation === 'remote' ? 'Remote' : 'Hybrid'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Action Attempted
              </Typography>
              <Typography variant="body1">
                {request.action === 'clock_in' ? 'Clock In' : request.action}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Requested Date
              </Typography>
              <Typography variant="body1">
                {new Date(request.createdAt).toLocaleString()}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            CIDR Format
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Enter the IP address in CIDR notation. Default is subnet format (/24):
            </Typography>
            <Typography variant="body2" component="div" sx={{ mt: 1, fontFamily: 'monospace' }}>
              • Subnet (recommended): {request.ipAddress.split('.').slice(0, 3).join('.')}.0/24<br />
              • Single IP: {request.ipAddress}/32
            </Typography>
          </Alert>

          <TextField
            fullWidth
            label="CIDR Format"
            value={finalCidr}
            onChange={(e) => setFinalCidr(e.target.value)}
            placeholder={`${request.ipAddress.split('.').slice(0, 3).join('.')}.0/24`}
            helperText="Format: x.x.x.x/xx (default: subnet /24, e.g., 192.168.1.0/24)"
            sx={{ mb: 3 }}
            error={finalCidr.trim() !== '' && !/^(?:25[0-5]|2[0-4]\d|1?\d?\d)(?:\.(?:25[0-5]|2[0-4]\d|1?\d?\d)){3}\/(?:3[0-2]|[12]?\d)$/.test(finalCidr.trim())}
          />

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={approveMutation.isPending || rejectMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<Cancel />}
              onClick={handleReject}
              disabled={approveMutation.isPending || rejectMutation.isPending}
            >
              Reject
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircle />}
              onClick={handleApprove}
              disabled={approveMutation.isPending || rejectMutation.isPending || !finalCidr.trim()}
            >
              {approveMutation.isPending ? 'Approving...' : 'Approve & Add IP'}
            </Button>
          </Box>

          {rejectMutation.isPending && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default IpApprovalPage;

